/**
 * Utility functions for working with Picsart Replay data
 *
 * For displaying images in the import popup: Uses raw image URLs (via resolveLocalUrl)
 * to show the actual image files, not the positioned/rendered versions.
 *
 * For canvas screenshots: Uses generateResultImage with over:true to capture the full canvas.
 */

import { getContext, isContextAvailable } from '../miniappContext';

import { resolveLocalUrl } from './localUrlResolver';

/**
 * Minimal structural type for the replay layer shape we actually read.
 * Sourcing the full type from @picsart/web-layering is brittle (deep, mutating
 * across SDK versions) — this narrows the API to the fields we depend on.
 */
interface ReplayLayer {
  name?: string;
  type?: string;
  mediaType?: string;
  props?: {
    image?: {
      value?: {
        location?: string;
        width?: number;
        height?: number;
      };
    };
  };
}

/**
 * Represents an image extracted from the replay/canvas
 */
export interface CanvasImage {
  id: string;
  name: string;
  url: string;
  width?: number;
  height?: number;
}

/**
 * Get the raw image URL for a layer (without canvas position/background).
 * Used for displaying images in the import popup.
 */
const getRawLayerImageUrl = (rawUrl: string): string => {
  const resolved = resolveLocalUrl(rawUrl);
  return resolved;
};

/**
 * Extracts image layers from the current Picsart canvas replay data.
 * Uses raw image URLs (not positioned/rendered) for proper display in the import popup.
 * Filters to only include user-added image layers (excludes background, system layers).
 *
 * @returns Array of CanvasImage objects with raw image URLs and metadata
 */
export const extractImagesFromReplay = async (): Promise<CanvasImage[]> => {
  try {
    if (!isContextAvailable()) {
      console.warn('[replayUtils] Context not available');
      return [];
    }

    const context = getContext();

    // Check if scene is available
    if (!context.scene) {
      console.warn('[replayUtils] Scene not available');
      return [];
    }

    // Sync to ensure we have the latest data (with error handling)
    try {
      if (context.scene.sync) {
        await context.scene.sync();
      }
    } catch {
      console.warn('[replayUtils] Scene sync failed, using cached data');
    }

    const replay = context.scene.replay;

    if (!replay?.context?.layers) {
      return [];
    }

    const layers = replay.context.layers as Record<string, ReplayLayer>;

    // First pass: identify image layers
    const imageLayers: Array<{ id: string; layer: ReplayLayer; rawUrl: string }> = [];

    for (const layerId of Object.keys(layers)) {
      const layer = layers[layerId];

      // Skip non-image layers
      if (!isImageLayer(layer)) {
        continue;
      }

      // Check if layer has image data (even if URL might not be directly accessible)
      const rawUrl = layer.props?.image?.value?.location;
      if (!rawUrl) {
        continue;
      }

      // Skip placeholder/system images
      if (isSystemImage(rawUrl)) {
        continue;
      }

      imageLayers.push({ id: layerId, layer, rawUrl });
    }

    // Second pass: resolve raw URLs for each layer (no generateResultImage - we want the raw image)
    const images: CanvasImage[] = [];

    for (let i = 0; i < imageLayers.length; i++) {
      const { id: layerId, layer, rawUrl } = imageLayers[i];

      // Use the raw image URL directly (resolved for iOS bare filenames)
      const imageUrl = getRawLayerImageUrl(rawUrl);

      if (imageUrl) {
        images.push({
          id: layerId,
          name: layer.name || `Image ${images.length + 1}`,
          url: imageUrl,
          width: layer.props?.image?.value?.width,
          height: layer.props?.image?.value?.height,
        });
      }
    }

    return images;
  } catch (error) {
    console.error('[replayUtils] Error extracting images:', error);
    return [];
  }
};

/**
 * Check if a layer is an image layer
 */
const isImageLayer = (layer: ReplayLayer): boolean => {
  // Check by media type
  if (layer.mediaType === 'PHOTO' || layer.mediaType === 'IMAGE') {
    return true;
  }

  // Check by type
  if (layer.type === 'image' || layer.type === 'PHOTO') {
    return true;
  }

  // Check if it has image props with a location
  if (layer.props?.image?.value?.location) {
    return true;
  }

  return false;
};

/**
 * Check if an image URL is a system/placeholder image that should be excluded
 */
const isSystemImage = (url: string): boolean => {
  // Skip common placeholder/system patterns
  const systemPatterns = [
    'placeholder',
    'blank',
    'empty',
    'transparent.png',
    'data:image/gif;base64,R0lGODlhAQAB', // 1x1 transparent gif
  ];

  const lowerUrl = url.toLowerCase();
  return systemPatterns.some(pattern => lowerUrl.includes(pattern));
};

/**
 * Capture a screenshot of the current canvas
 * @returns CanvasImage with the screenshot or null if capture fails
 */
export const captureCanvasScreenshot = async (): Promise<CanvasImage | null> => {
  try {
    if (!isContextAvailable()) {
      console.warn('[replayUtils] Context not available for screenshot');
      return null;
    }

    const context = getContext();

    // Check if scene and handlers are available
    if (!context.scene || !context.handlers?.generateResultImage) {
      console.warn('[replayUtils] Scene or handlers not available for screenshot');
      return null;
    }

    const targetCanvasId = context.scene.selectedCanvasId;

    const resource = await context.handlers.generateResultImage(targetCanvasId, {
      over: true,
    });

    if (resource?.location && resource.location !== 'data:,' && resource.location.length > 20) {
      return {
        id: '__screenshot__',
        name: 'Full Canvas',
        url: resource.location,
      };
    }

    console.warn('[replayUtils] Screenshot capture returned empty data');
    return null;
  } catch (error) {
    console.error('[replayUtils] Failed to capture screenshot:', error);
    return null;
  }
};

/**
 * Check if an image name indicates a background layer
 */
const isBackgroundLayer = (name: string): boolean => {
  const nameLower = name.toLowerCase();
  return nameLower.includes('background') || nameLower.includes('bg_') || nameLower === 'bg';
};

/**
 * Get all available images from the canvas (without screenshot)
 * @returns Array of CanvasImage objects from layers only
 */
export const getCanvasImages = async (): Promise<CanvasImage[]> => {
  return extractImagesFromReplay();
};

/**
 * Get all available images from the canvas: individual layers + full canvas screenshot
 * Only includes screenshot if there are actual layer images (to avoid blank canvas screenshots)
 * @returns Array of CanvasImage objects
 */
export const getCanvasImagesWithScreenshot = async (): Promise<CanvasImage[]> => {
  // Get individual image layers
  const layerImages = await extractImagesFromReplay();

  // Only include screenshot if there are actual layer images
  // This prevents showing dialog for blank/empty canvases
  if (layerImages.length === 0) {
    return [];
  }

  // Skip screenshot if only 1 image and it's a background (screenshot would be identical)
  if (layerImages.length === 1 && isBackgroundLayer(layerImages[0].name)) {
    return layerImages;
  }

  // Capture full canvas screenshot
  const screenshot = await captureCanvasScreenshot();

  // Combine: layer images first, screenshot last
  const allImages = [...layerImages];
  if (screenshot) {
    allImages.push(screenshot);
  }

  return allImages;
};

/**
 * Check if there's exactly one image layer in the canvas.
 * Used for auto-import scenario where dialog can be skipped.
 * @returns The single CanvasImage if exactly one image exists, null otherwise
 */
export const getSingleLayerImage = async (): Promise<CanvasImage | null> => {
  try {
    const images = await extractImagesFromReplay();

    if (images.length === 1) {
      return images[0];
    }

    return null;
  } catch (error) {
    console.error('[replayUtils] Error checking for single layer:', error);
    return null;
  }
};

export default extractImagesFromReplay;
